import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { FilePdf, DownloadSimple, Info, ArrowSquareOut, CheckCircle } from "@phosphor-icons/react";
import { api, documentDownloadUrl } from "@/lib/api";
import { humanSize, frShort } from "@/lib/format";
import PageHeader from "@/components/PageHeader";

const DEFAULT_STEPS = [
    "Téléchargez et complétez le dossier d'inscription ci-dessous.",
    "Joignez un questionnaire de santé (QS-Sport) dûment rempli.",
    "Réglez la cotisation (chèque, espèces ou Pass'Sport accepté).",
    "Déposez le dossier complet au club-house ou adhérez en ligne sur Ten'Up.",
];

export default function Inscriptions() {
    const { data: docs = [], isLoading } = useQuery({ queryKey: ["documents"], queryFn: async () => (await api.get("/documents")).data });
    const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
    const steps = (settings?.inscription_steps && settings.inscription_steps.length) ? settings.inscription_steps : DEFAULT_STEPS;

    return (
        <div data-testid="inscriptions-page">
            <PageHeader
                testid="inscriptions-header"
                overline="Rejoignez-nous"
                title="Inscriptions & documents"
                subtitle={settings?.season_info || "Toutes les informations et documents pour vous inscrire au club pour la nouvelle saison."}
            />

            <div className="max-w-6xl mx-auto px-4 md:px-6 py-12 grid gap-10 lg:grid-cols-5">
                {/* Steps */}
                <div className="lg:col-span-2">
                    <div className="brand-card p-6 md:p-8 sticky top-24">
                        <div className="flex items-center gap-2 overline text-clay mb-5">
                            <Info weight="bold" size={16} /> Comment s'inscrire
                        </div>
                        <ol className="space-y-5">
                            {steps.map((s, i) => (
                                <li key={i} className="flex gap-4">
                                    <span className="shrink-0 grid place-items-center h-8 w-8 rounded-full bg-navy text-white font-mono font-bold text-sm">
                                        {i + 1}
                                    </span>
                                    <span className="text-slate-600 text-sm leading-relaxed pt-1">{s}</span>
                                </li>
                            ))}
                        </ol>
                        <a
                            href={settings?.tenup_url || "https://tenup.fft.fr"}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="inscriptions-tenup-btn"
                            className="mt-7 inline-flex items-center gap-2 rounded-full bg-clay px-5 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-clay-dark"
                        >
                            Créer ma licence sur Ten'Up <ArrowSquareOut weight="bold" size={16} />
                        </a>
                    </div>
                </div>

                {/* Documents */}
                <div className="lg:col-span-3">
                    <h2 className="font-display font-extrabold text-2xl text-navy mb-5 tracking-tight">Documents à télécharger</h2>
                    {isLoading ? (
                        <p className="text-slate-500 font-mono text-sm">Chargement…</p>
                    ) : docs.length === 0 ? (
                        <div className="brand-card p-8 text-center text-slate-500" data-testid="documents-empty">
                            <FilePdf weight="duotone" size={40} className="mx-auto text-slate-300 mb-3" />
                            Les documents seront disponibles très prochainement.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {docs.map((d, i) => (
                                <motion.a
                                    key={d.id}
                                    href={documentDownloadUrl(d.id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="document-download-item"
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="group flex items-center gap-4 brand-card p-4 md:p-5 transition-colors duration-200 hover:border-clay"
                                >
                                    <span className="shrink-0 grid place-items-center h-12 w-12 rounded-lg bg-clay/10 text-clay">
                                        <FilePdf weight="duotone" size={26} />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-[10px] uppercase tracking-widest text-clay">{d.category}</span>
                                            <span className="text-xs text-slate-400">{frShort(d.created_at)}</span>
                                        </div>
                                        <div className="font-display font-bold text-navy truncate">{d.title}</div>
                                        {d.description && <div className="text-sm text-slate-500 truncate">{d.description}</div>}
                                        <div className="text-xs text-slate-400 mt-0.5">{d.original_filename} · {humanSize(d.size)}</div>
                                    </div>
                                    <span className="shrink-0 grid place-items-center h-10 w-10 rounded-full bg-navy text-white transition-transform duration-200 group-hover:-translate-y-0.5">
                                        <DownloadSimple weight="bold" size={18} />
                                    </span>
                                </motion.a>
                            ))}
                        </div>
                    )}

                    <div className="mt-8 brand-card p-6 bg-slate-50 flex items-start gap-3">
                        <CheckCircle weight="fill" size={22} className="text-clay shrink-0 mt-0.5" />
                        <p className="text-sm text-slate-600 leading-relaxed">
                            Une question sur votre inscription ? Contactez-nous par email à{" "}
                            <a href={`mailto:${settings?.email || "tennislaturballe@gmail.com"}`} className="text-navy font-bold hover:text-clay">
                                {settings?.email || "tennislaturballe@gmail.com"}
                            </a>{" "}
                            ou passez directement au club-house.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
